import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

interface DiscountPolicy {
  admissionFeeDiscountMax: number;
  monthlyFeeDiscountMax: number;
  booksDiscountMax: number;
  uniformDiscountMax: number;
  orphanMonthlyDiscount: number;
  orphanAdmissionDiscount: number;
  islamicScholarMonthlyDiscount: number;
  islamicScholarAdmissionDiscount: number;
  siblingAdmissionDiscount: number;
  siblingMonthlyDiscount: number;
  staffChildMonthlyDiscount: number;
  staffChildAdmissionDiscount: number;
  first100StudentsAdmissionDiscount: number;
}

interface DiscountSelection {
  orphan: boolean;
  islamicScholar: boolean;
  hasSiblings: boolean;
  staffChild: boolean;
  customDiscountType: 'admission' | 'monthly' | 'books' | 'uniform' | '';
  customDiscountAmount: number;
  customDiscountReason: string;
}

interface DiscountPolicyDisplayProps {
  discountPolicy: DiscountPolicy;
  onDiscountChange: (selection: DiscountSelection) => void;
  className?: string;
}

const DiscountPolicyDisplay: React.FC<DiscountPolicyDisplayProps> = ({
  discountPolicy,
  onDiscountChange,
  className = ''
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selection, setSelection] = useState<DiscountSelection>({
    orphan: false,
    islamicScholar: false,
    hasSiblings: false,
    staffChild: false,
    customDiscountType: '',
    customDiscountAmount: 0,
    customDiscountReason: ''
  });

  const handleEligibilityChange = (field: keyof Omit<DiscountSelection, 'customDiscountType' | 'customDiscountAmount' | 'customDiscountReason'>) => {
    const updated = { ...selection, [field]: !selection[field] };
    setSelection(updated);
    onDiscountChange(updated);
  };

  const handleCustomDiscountChange = (field: string, value: any) => {
    const updated = { ...selection, [field]: value };
    setSelection(updated);
    onDiscountChange(updated);
  };

  return (
    <div className={`bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-200 rounded-lg flex items-center justify-center text-purple-700 font-bold">
            💰
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Discount Eligibility & Options</h3>
            <p className="text-sm text-slate-600">Select applicable discounts for this student</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-slate-600 hover:text-slate-800 transition-colors"
        >
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {(
        <div className="mt-6 space-y-6">
          {/* Policy Summary */}
          <div className="bg-white rounded-lg p-4 border border-purple-100">
            <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider mb-3">Available Discounts Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="bg-purple-50 p-2 rounded">
                <div className="font-semibold text-purple-700">Admission</div>
                <div className="text-purple-600">Max ₹{discountPolicy.admissionFeeDiscountMax}</div>
              </div>
              <div className="bg-blue-50 p-2 rounded">
                <div className="font-semibold text-blue-700">Monthly Fee</div>
                <div className="text-blue-600">Max {discountPolicy.monthlyFeeDiscountMax}%</div>
              </div>
              <div className="bg-green-50 p-2 rounded">
                <div className="font-semibold text-green-700">Books</div>
                <div className="text-green-600">Max ₹{discountPolicy.booksDiscountMax}</div>
              </div>
              <div className="bg-amber-50 p-2 rounded">
                <div className="font-semibold text-amber-700">Uniform</div>
                <div className="text-amber-600">Max ₹{discountPolicy.uniformDiscountMax}</div>
              </div>
              <div className="bg-red-50 p-2 rounded">
                <div className="font-semibold text-red-700">Orphan</div>
                <div className="text-red-600">{discountPolicy.orphanMonthlyDiscount}% + {discountPolicy.orphanAdmissionDiscount}%</div>
              </div>
              <div className="bg-indigo-50 p-2 rounded">
                <div className="font-semibold text-indigo-700">Staff Child</div>
                <div className="text-indigo-600">{discountPolicy.staffChildMonthlyDiscount}% + {discountPolicy.staffChildAdmissionDiscount}%</div>
              </div>
            </div>
          </div>

          {/* Eligibility-Based Discounts */}
          <div className="space-y-3">
            <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Eligibility-Based Discounts</h4>

            {/* Orphan Status */}
            <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={selection.orphan}
                onChange={() => handleEligibilityChange('orphan')}
                className="w-5 h-5 text-purple-600 rounded mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-slate-800">Orphan Status</div>
                <div className="text-sm text-slate-600">
                  {discountPolicy.orphanMonthlyDiscount}% monthly fee discount + {discountPolicy.orphanAdmissionDiscount}% admission fee discount
                </div>
              </div>
            </label>

            {/* Islamic Scholar */}
            <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={selection.islamicScholar}
                onChange={() => handleEligibilityChange('islamicScholar')}
                className="w-5 h-5 text-purple-600 rounded mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-slate-800">Islamic Scholar</div>
                <div className="text-sm text-slate-600">
                  {discountPolicy.islamicScholarMonthlyDiscount}% monthly fee discount + {discountPolicy.islamicScholarAdmissionDiscount}% admission fee discount
                </div>
              </div>
            </label>

            {/* Has Siblings */}
            <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={selection.hasSiblings}
                onChange={() => handleEligibilityChange('hasSiblings')}
                className="w-5 h-5 text-purple-600 rounded mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-slate-800">Has Siblings in School</div>
                <div className="text-sm text-slate-600">
                  {discountPolicy.siblingAdmissionDiscount === 100 ? '✅ Admission fee waived' : `${discountPolicy.siblingAdmissionDiscount}% admission discount`} OR {discountPolicy.siblingMonthlyDiscount}% monthly fee discount
                </div>
              </div>
            </label>

            {/* Staff Child */}
            <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={selection.staffChild}
                onChange={() => handleEligibilityChange('staffChild')}
                className="w-5 h-5 text-purple-600 rounded mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-slate-800">Staff Child</div>
                <div className="text-sm text-slate-600">
                  {discountPolicy.staffChildMonthlyDiscount}% monthly fee discount + {discountPolicy.staffChildAdmissionDiscount}% admission fee discount
                </div>
              </div>
            </label>
          </div>

          {/* Custom Discount Section */}
          <div className="border-t border-purple-200 pt-6">
            <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider mb-3">Custom Discount (Sales/Special)</h4>
            <div className="space-y-3">
              {/* Discount Type */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Discount Type</label>
                <select
                  value={selection.customDiscountType}
                  onChange={(e) => handleCustomDiscountChange('customDiscountType', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value="">Select discount type</option>
                  <option value="admission">Admission Fee Discount (Max: ₹{discountPolicy.admissionFeeDiscountMax})</option>
                  <option value="monthly">Monthly Fee Discount (Max: {discountPolicy.monthlyFeeDiscountMax}%)</option>
                  <option value="books">Books/Copies Discount (Max: ₹{discountPolicy.booksDiscountMax})</option>
                  <option value="uniform">Uniform Discount (Max: ₹{discountPolicy.uniformDiscountMax})</option>
                </select>
              </div>

              {/* Discount Amount */}
              {selection.customDiscountType && (
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2">
                    {selection.customDiscountType === 'monthly' ? 'Discount Percentage (%)' : 'Discount Amount (₹)'}
                  </label>
                  <input
                    type="number"
                    value={selection.customDiscountAmount}
                    onChange={(e) => handleCustomDiscountChange('customDiscountAmount', parseFloat(e.target.value) || 0)}
                    className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder={selection.customDiscountType === 'monthly' ? 'e.g., 25' : 'e.g., 2500'}
                    max={
                      selection.customDiscountType === 'admission'
                        ? discountPolicy.admissionFeeDiscountMax
                        : selection.customDiscountType === 'monthly'
                        ? discountPolicy.monthlyFeeDiscountMax
                        : selection.customDiscountType === 'books'
                        ? discountPolicy.booksDiscountMax
                        : discountPolicy.uniformDiscountMax
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Max allowed:{' '}
                    {selection.customDiscountType === 'admission'
                      ? `₹${discountPolicy.admissionFeeDiscountMax}`
                      : selection.customDiscountType === 'monthly'
                      ? `${discountPolicy.monthlyFeeDiscountMax}%`
                      : selection.customDiscountType === 'books'
                      ? `₹${discountPolicy.booksDiscountMax}`
                      : `₹${discountPolicy.uniformDiscountMax}`}
                  </p>
                </div>
              )}

              {/* Discount Reason */}
              {selection.customDiscountType && (
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2">Reason for Discount</label>
                  <select
                    value={selection.customDiscountReason}
                    onChange={(e) => handleCustomDiscountChange('customDiscountReason', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 outline-none"
                  >
                    <option value="">Select reason</option>
                    <option value="sales_incentive">Sales Incentive</option>
                    <option value="scholarship">Scholarship</option>
                    <option value="hardship">Financial Hardship</option>
                    <option value="referral">Referral Bonus</option>
                    <option value="early_bird">Early Bird Admission</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Note:</strong> Eligibility-based discounts and custom discounts can be combined. The system will apply the most beneficial combination for the student.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountPolicyDisplay;
